import React, { useState, useEffect, useContext, useRef } from 'react';
import ModalEliminacion from '../modal/ModalEliminacion';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { notifySuccess, notifyError } from '../utils/toast';

interface ClienteProps 
{
  client?: 
  { 
    id: number; 
    name: string; 
    login: string; 
    password: string 
  };

  onSave: (clientData: 
  { 
    id?: number;
    name: string; 
    login?: string; 
    password: string 
  }) => void;

  onDelete?: (clientId: number) => void;

  onCancel: () => void;
}

const Recinto: React.FC<ClienteProps> = ({ client, onSave, onDelete, onCancel }) => 
{
  const { token } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Estado para el modal
  const modalRef = useRef<HTMLDivElement>(null); // Ref para el contenedor del modal
  
  // Determinar si estamos en modo de edición o creación
  const isEditing = client && client.id !== -1;

  //-----------------------------------------------------------

  useEffect(() => 
  {
    if (client) 
    {
      setName(client.name);
      setLogin(client.login);
      setPassword(client.password);
    }
    else
    {
      setName('');
      setLogin('');
      setPassword('');
    }
  }, [client]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    // Función para manejar clics fuera del componente
    const handleClickOutside = (event: MouseEvent) => 
    {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    // Función para manejar presiones de teclas
    const handleEscPress = (event: KeyboardEvent) => 
    {
      if (event.key === 'Escape') 
      {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscPress);

    return () => 
    {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscPress);
    };
  }, [onCancel]);  

  //-----------------------------------------------------------

  const handleSubmit = async (event: React.FormEvent) => 
  {
    event.preventDefault();
    try 
    {
      const formData = new URLSearchParams();

      formData.append('name', name);
      formData.append('password', password);
  
      if (!isEditing)
        formData.append('login', login);
      
      if (isEditing && client)
        formData.append('id', client.id.toString());
  
      const url = isEditing ? 'web/update_client' : 'web/create_client';

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      if (response.ok) 
      {
        onSave({ 
          id: client?.id ?? undefined, 
          name, 
          login, 
          password });

        notifySuccess('Cliente guardado correctamente');
      } 
      else 
      {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar el cliente.');
      }
    } 
    catch (error) 
    {
      setError('Error de red. Por favor, intente nuevamente.');
    }
  };

  //-----------------------------------------------------------

  const handleDelete = () => 
  {
    if (client && client.id !== null && client.id !== -1) 
      {
        setIsModalOpen(true);
      }
  };

  //-----------------------------------------------------------

  const handleConfirmDelete = async () => 
  {
    if (client && client.id !== null && client.id !== -1 && onDelete) 
    {
      try 
      {
        const formData = new URLSearchParams();
        formData.append('id', client.id.toString());
  
        const response = await apiFetch('web/delete_client', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'},
          body: formData.toString()});
          
        if (response.ok) 
        {
          onDelete(client.id); // Notificar a Clientes.tsx que el cliente fue eliminado
          setIsModalOpen(false); // Cerrar el modal
          notifySuccess('Cliente eliminado correctamente');
          
        } 
        else 
        {
          const errorData = await response.json();
          setError(errorData.error || 'Error al eliminar el cliente.');
        }
      }
      catch (error) 
      {
        setError('Error de red. Por favor, intente nuevamente.');
      }
    }
  };

  //-----------------------------------------------------------

  return (
    <div className="evento-container" ref={modalRef}>
      <button type="button" className="modal-close-button" onClick={onCancel} aria-label="Cerrar">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
        </svg>
      </button>
      <h2>{isEditing ? 'Editar Recinto' : 'Crear Nuevo Recinto'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre del recinto"
              required
            />
        </div>
        <div className="form-group">
          <input
              id="login"
              type="text"
              value={login}
              onChange={(e) => {
                if (!isEditing) {
                  setLogin(e.target.value);
                }
              }}
              placeholder="Login App"
              required
              disabled={isEditing}
            />
        </div>
        <div className="form-group">
          <input
              id="password"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password App"
              required
            />          
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="form-actions">
          {isEditing && client && client.id !== null && client.id !== -1 && (
            <button type="button" className="delete-event-button" onClick={handleDelete}>
              <i className="fas fa-trash"></i>Borrar
            </button>
          )}
          <div className="form-actions-right">
            <button type="button" onClick={onCancel}>Cancelar</button>
            <button type="submit" className='save-button'>{isEditing ? 'Guardar' : 'Crear'}</button>             
          </div>                   
        </div>
      </form>
      {isModalOpen && (
        <ModalEliminacion
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}>
          <p>¿Estás seguro de que quieres eliminar este recinto?</p>
        </ModalEliminacion>
      )}
    </div>
  );
};

export default Recinto;
